import React, { ReactNode, useState } from "react"
import styled, { css } from "styled-components"
import { Box, Row } from "@jobintrans/base-ui.components.atoms._atoms"
import { BaseStyles } from "@jobintrans/base-ui.types.base-styles"
import { H3, Label } from "@jobintrans/base-ui.components.atoms.typography"
import { IconButton } from "@jobintrans/base-ui.components.atoms.button"
import Icon, { IconBox } from "@jobintrans/base-ui.components.atoms.icon"

export const AccordionCheckbox = ({
  disabled,
  checked,
  onClick,
  $borderColor,
  ...rest
}: {
  disabled?: boolean
  checked: boolean
  onClick: () => void
} & BaseStyles) => {
  return (
    <CheckWrapper
      onClick={disabled ? () => {} : onClick}
      $active={!disabled && checked}
      $disabled={disabled}
      $borderColor={$borderColor}
      {...rest}
    >
      <Icon icon="check" $size="10px" $color="white" $opacity={checked ? 1 : 0} />
    </CheckWrapper>
  )
}

const Accordion = ({
  disabled = false,
  icon,
  title,
  children,
  isCollapsible = false,
  checked = false,
  helperText,
  onClick = () => {},
  isCheckable = false,
  $borderColor,
  $outerBorderColor,
  $showBorder = true,
  $withBackground = false,
  ...rest
}: BaseStyles & {
  disabled?: boolean
  icon?: string
  title: string
  helperText?: string
  isCollapsible?: boolean
  checked?: boolean
  onClick?: () => void
  isCheckable?: boolean
  children?: ReactNode
  $outerBorderColor?: string
  $showBorder?: boolean
  $withBackground?: boolean
}) => {
  const [active, setActive] = useState(true)

  return (
    <Wrapper
      $showBorder={$showBorder}
      $withBackground={$withBackground}
      $hasHelperText={!!helperText}
      $borderColor={$outerBorderColor ?? $borderColor}
      {...rest}
    >
      {helperText && (
        <HelperWrapper
          $padding="XS"
          $justifyContent="center"
          $background="primary"
          $marginLeft="-1px"
          $marginTop="-1px"
        >
          <Label $type="third" $color="white">
            {helperText}
          </Label>
        </HelperWrapper>
      )}
      <Header>
        <Row $alignItems="center" $opacity={disabled ? 0.6 : 1}>
          {icon && <IconBox icon={icon} $small $marginRight="S" />}
          <H3 $marginBottom="0">{title}</H3>
        </Row>
        {isCollapsible && (
          <IconButton
            ariaLabel={active ? "See less" : "See more"}
            icon={active ? "arrow-top" : "arrow-bottom"}
            onClick={() => setActive(!active)}
          />
        )}
        {isCheckable && (
          <AccordionCheckbox
            checked={checked}
            disabled={disabled}
            onClick={disabled ? () => {} : onClick}
            $borderColor={$borderColor}
          />
        )}
      </Header>
      {active && <Content $borderColor={$borderColor}>{children}</Content>}
    </Wrapper>
  )
}

export default Accordion

const CheckWrapper = styled(Box)<{
  $active: boolean
  $disabled?: boolean
}>`
  width: 25px;
  height: 25px;
  border: 1px solid ${({ theme, $borderColor = "border" }) => theme.color[$borderColor]};
  background: ${({ theme, $background = "none" }) => theme.color[$background] ?? $background};
  border-radius: 50%;
  transition: all 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;

  ${({ $active }) =>
    $active &&
    css`
      border: 1px solid ${({ theme }) => theme.color.primary};
      background: ${({ theme }) => theme.color.primary};
      cursor: pointer;
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
    `}
`

const Content = styled(Box)`
  padding: ${({ theme }) => theme.space.S};
  border-top: 1px solid ${({ theme, $borderColor = "border" }) => theme.color[$borderColor]};
`

const Header = styled(Box)`
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ theme }) => theme.space.XS} ${({ theme }) => theme.space.S};
`

const HelperWrapper = styled(Row)`
  width: calc(100% + 2px);
  position: absolute;
  left: 0;
  top: 0;
  transform: translateY(-100%);
  border-radius: ${({ theme }) =>
    `${theme.variable.borderRadiusOuter} ${theme.variable.borderRadiusOuter} 0 0 `};
`

const Wrapper = styled(Box)<{
  $showBorder: boolean
  $withBackground: boolean
  $hasHelperText: boolean
}>`
  border-radius: ${({ theme, $hasHelperText }) =>
    $hasHelperText
      ? `0 0 ${theme.variable.borderRadiusOuter} ${theme.variable.borderRadiusOuter}`
      : theme.variable.borderRadiusOuter};

  ${({ $showBorder, $borderColor = "border" }) =>
    $showBorder &&
    css`
      border: 1px solid ${({ theme }) => theme.color[$borderColor]};
    `};

  ${({ $withBackground }) =>
    $withBackground &&
    css`
      background: white;
    `};

  ${({ theme, $background }) =>
    $background &&
    css`
      background: ${theme.color[$background]};
    `};
`
